import { ClassType, EntityType } from '@frontend/common';
import { DefaultBadge } from '@frontend/components/badge';
import { StatusBadge, accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { CommonMessage } from '@frontend/lang';
import { SlotExtendedClient } from '@frontend/slot/api';
import { SlotExtended, SlotStatus } from '@frontend/slot/types';

import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import OverviewHeader from '../../header/overview-header';
import { useSlots } from './slots.controller';

export const Slots = () => {
    const viewProps = useSlots();
    const { openPlatformDetail } = usePlatformModalHost();
    return (
        <div className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader
                title={
                    <div className='flex gap-2'>
                        <span>{CommonMessage.OBJECTS.SPOT.DISTRI_SPOTS}</span>
                        <span>/</span>
                        <span>{CommonMessage.OBJECTS.SLOT.PLURAL}</span>
                    </div>
                }
            />

            <ReactiveObjectList<SlotExtended>
                key='slots-list'
                id={'slots-list'}
                defaultListValues={{ sorting: [{ id: 'display', desc: false }] }}
                fetch={(arg) => SlotExtendedClient.fetchSlotExtendeds(arg)}
                dispatch={viewProps.dispatch}
                onSelectElement={(obj) =>
                    openPlatformDetail({
                        entityType: EntityType.SLOT,
                        id: obj.id,
                        parents: {
                            ...(obj.spot?.entity_id && { spot_id: obj.spot.entity_id }),
                            ...(obj.module?.entity_id && { module_id: obj.module.entity_id })
                        }
                    })
                }
                type={EntityType.SLOT}
                columns={{
                    columns: ['spot', 'module', 'index', 'display', 'type', 'status'],
                    customCells: new Map([
                        [
                            'status',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <StatusBadge
                                        value={obj.row.original.status}
                                        colorMap={
                                            new Map([
                                                [[SlotStatus.BROKEN], ClassType.DANGER],
                                                [[SlotStatus.AVAILABLE], ClassType.SUCCESS],
                                                [[SlotStatus.MERGED], ClassType.WARNING]
                                            ])
                                        }
                                        displayMap={
                                            new Map([
                                                [
                                                    obj.row.original.status,
                                                    (obj.row.original.status.charAt(0).toUpperCase() + obj.row.original.status.slice(1)).replace(/_/g, ' ')
                                                ]
                                            ])
                                        }
                                    />
                                </td>
                            )
                        ],
                        [
                            'type',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    {obj.row.original.type && (
                                        <DefaultBadge
                                            label={(obj.row.original.type.charAt(0).toUpperCase() + obj.row.original.type.slice(1)).replace(/_/g, ' ')}
                                            styles={{
                                                container: {
                                                    color: 'bg-brand-muted'
                                                }
                                            }}
                                        />
                                    )}
                                </td>
                            )
                        ],
                        [
                            'spot',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <PlatformObjectBadge
                                        id={obj.row.original.spot?.entity_id}
                                        entityType={EntityType.SPOT}
                                        display={obj.row.original.spot?.name}
                                        key={obj.row.original.spot?.entity_id}
                                        foreign={obj.row.original.spot?.foreign}
                                        accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                    />
                                </td>
                            )
                        ],
                        [
                            'module',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <PlatformObjectBadge
                                        id={obj.row.original.module?.entity_id}
                                        entityType={EntityType.MODULE}
                                        display={obj.row.original.module?.name}
                                        key={obj.row.original.module?.entity_id}
                                        parentIds={{
                                            spot_id: obj.row.original.spot_id
                                        }}
                                        foreign={obj.row.original.module?.foreign}
                                        accountId={accountIdFromResolvedEntity(obj.row.original.module)}
                                    />
                                </td>
                            )
                        ]
                    ]),
                    headerOverwrite: new Map([
                        ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                        ['module', CommonMessage.OBJECTS.MODULE.DEFAULT]
                    ])
                }}
                activeFilter={{
                    searchKeys: ['spot', 'display'],
                    filterKeys: ['spot_id', 'module_id'],
                    displayName: new Map([['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT]])
                }}
                sortingColumns={['index', 'display']}
            />
        </div>
    );
};
